import React, { ReactNode } from 'react';

const colors = {
    key: '#0451a5',
    string: '#a31515',
    number: '#098658',
    boolean: '#0000ff',
    null: '#0000ff',
    punctuation: '#333333',
};

const punctuation = (value: string) => (
    <span key={value} style={{ color: colors.punctuation }}>
        {value}
    </span>
);

export const renderJsonHighlight = (value: unknown, indent = 0): ReactNode => {
    const currentIndent = '  '.repeat(indent);
    const childIndent = '  '.repeat(indent + 1);

    if (value === null) {
        return <span style={{ color: colors.null }}>null</span>;
    }

    if (typeof value === 'boolean') {
        return <span style={{ color: colors.boolean }}>{String(value)}</span>;
    }

    if (typeof value === 'number') {
        return <span style={{ color: colors.number }}>{value}</span>;
    }

    if (typeof value === 'string') {
        return <span style={{ color: colors.string }}>&quot;{value}&quot;</span>;
    }

    if (Array.isArray(value)) {
        if (!value.length) {
            return <>{punctuation('[]')}</>;
        }

        return (
            <>
                {punctuation('[')}
                {'\n'}
                {value.map((item, index) => (
                    <React.Fragment key={index}>
                        {childIndent}
                        {renderJsonHighlight(item, indent + 1)}
                        {index < value.length - 1 ? punctuation(',') : ''}
                        {'\n'}
                    </React.Fragment>
                ))}
                {currentIndent}
                {punctuation(']')}
            </>
        );
    }

    if (typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>);
        if (!entries.length) {
            return <>{punctuation('{}')}</>;
        }

        return (
            <>
                {punctuation('{')}
                {'\n'}
                {entries.map(([key, item], index) => (
                    <React.Fragment key={key}>
                        {childIndent}
                        <span style={{ color: colors.key }}>&quot;{key}&quot;</span>
                        {punctuation(': ')}
                        {renderJsonHighlight(item, indent + 1)}
                        {index < entries.length - 1 ? punctuation(',') : ''}
                        {'\n'}
                    </React.Fragment>
                ))}
                {currentIndent}
                {punctuation('}')}
            </>
        );
    }

    return <span>{String(value)}</span>;
};

export const parseJsonValue = (value: string): unknown | null => {
    if (!value.trim()) {
        return null;
    }
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};
